import { Injectable } from "@angular/core";
import { BaseService } from "../../@core/services/base.service";
import { environment } from "../../../environments/environment.dev";
import { HttpClient } from "@angular/common/http";
import { IOrganization, IUser } from "../../@core/models/model";
import { IDocumentType, IQnaPairMetadata } from "../models/settings.model";
import { IFormQuestion, IFormAnswer, IContract, FORM_QUESTION_STATUS } from "../models/contract-management.model";

export interface IQnaPair {
    _id: string;
    organization: string | IOrganization;
    documentType: string | IDocumentType;
    question: string | IFormQuestion;
    answer: string | IFormAnswer;
    timesUsed: number;
    author: string | IUser; // created By
    document: string | IContract; // in case it was added through a document/contract
    sourceType: QNA_SOURCE_TYPE;
    metadata?: string | IQnaPairMetadata;
    createdAt: Date;
    updatedAt: Date;
}

export enum EXPORT_TYPE {
    EXCEL = 'EXCEL',
    CSV = 'CSV'
}

export enum QNA_SOURCE_TYPE {
    MANUAL = 'MANUAL',
    UPLOAD = 'UPLOAD',
    FORM = 'FORM'
}

export interface IQnaReviewFilterOptions {
    documentName: string[];
    author: { _id: string, fullName: string }[];
    sourceType: { label: string, value: QNA_SOURCE_TYPE }[];
}

export interface IQnaPairResponse {
    data: (IQnaPair & { reviewers: Partial<IUser>[] })[];
    totalRecords: number;
}

export interface IDuplicateClusterResponse {
    _id: string
    organization: string;
    questions: { _id: string, linkedQnAPair: IQnaPair & { reviewers: Partial<IUser>[] } }[];
}

@Injectable({ providedIn: 'root' })
export class QnAPairService extends BaseService {
    apiUrl = environment.apiUrl + '/qna';

    constructor(protected override http: HttpClient) {
        super(http);
    }

    getQnAPairs(data: {
        documentTypeId: string;
        searchValue: string;
        skip: number;
        limit: number;
        ids?: string[];
        filter?: {
            author?: string[];
            reviewer?: string[];
            minTimeUsed?: number | null;
            maxTimeUsed?: number | null;
            status?: FORM_QUESTION_STATUS[];
            documentName?: string[];
            sourceType?: QNA_SOURCE_TYPE[];
        };
    }) {
        return this.post<IQnaPairResponse>(this.apiUrl, data);
    }

    getQnACount(status: FORM_QUESTION_STATUS[]) {
        return this.post<{ [key: string]: number }>(this.apiUrl + '/qna-count', { status });
    }
    downloadQnaPairs(qnaPairIds: string[], type: EXPORT_TYPE) {
        return this.post<{ data: Buffer }>(this.apiUrl + '/download', { qnaPairIds, type });
    }

    deleteQnaPairs(qnaPairIds: string[]) {
        return this.delete(this.apiUrl + '/', { qnaPairIds });
    }

    bulkUpdateQnaPairs(qnaPairs: { id: string, question: string, answer: string, reviewer: string }[]) {
        return this.post(this.apiUrl + '/update', { updates: qnaPairs });
    }

    addManualQnAPair(question: string, answer: string, documentTypeId: string) {
        const payload: Partial<IQnaPair> = {
            question,
            answer,
            documentType: documentTypeId,
        };
        return this.post<IQnaPair>(this.apiUrl + '/addmanualqna', payload);
    }

    updateQnaPair(id: string, question: string, answer: string, reviewer: string) {
        return this.put(this.apiUrl + '/' + id, { question, answer, reviewer });
    }

    addQuestionToQnA(documentId: string, questionId: string, answerId: string) {
        return this.post<IQnaPair>(this.apiUrl + '/add-qna-from-document', { documentId, questionId, answerId })
    }

    bulkStatusUpdatesQnAPairs(
        contractId: string,
        questionIds: string[],
        status: FORM_QUESTION_STATUS,
        documentTypeId?: string
    ) {
        return this.post<{ questionsToFlag: string[], questionsToUnflag: string[] }>(this.apiUrl + '/bulk-status-update', {
            contractId,
            questionIds,
            status,
            documentTypeId
        });
    }

    extractQnaFromFiles(files: File[], documentTypeId: string) {
        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });
        formData.append('documentType', documentTypeId);
        return this.post(this.apiUrl + '/extract-from-files', formData);
    }

    /**
     * Re-trigger extraction for an existing QnaPairMetadata record (same _id)
     * after credits are topped up. Uses the stored PDF attachment, avoiding a
     * new upload.
     */
    retryQnaPairExtraction(qnaPairMetadataId: string) {
        return this.post(this.apiUrl + '/retry-extraction', { qnaPairMetadataId });
    }

    getUploadedFilesAudit(data: { documentType: string, limit: number, skip: number }) {
        return this.post<IQnaPairMetadata[]>(this.apiUrl + '/uploaded-files-audit', data);
    }

    getQnAReviewFilterOptions() {
        return this.get<IQnaReviewFilterOptions>(this.apiUrl + '/review-filter-options');
    }

    getDuplicateQuestions(documentTypeId: string) {
        return this.post<IDuplicateClusterResponse[]>(this.apiUrl + '/duplicate-questions', { documentTypeId });
    }

    createMergedQnaPair(payload: {
        question: string;
        answer: string;
        documentType: string;
        qnaPairIds: string[];
        accept: boolean;
    }) {
        return this.post<IQnaPair>(this.apiUrl + '/merge-questions', payload);
    }

    updateQuestionsAsNotDuplicate(questionIds: string[]) {
        return this.post(this.apiUrl + '/mark-not-duplicate', { questionIds });
    }

    acceptQuestionInCluster(questionId: string) {
        return this.post(this.apiUrl + '/accept-duplicate-question', { questionId });
    }

    deleteQuestionInCluster(questionIds: string[]) {
        return this.post(this.apiUrl + '/delete-duplicate-question', { questionIds });
    }

    bulkAddQnaPairsFromDocument(data: { documentId: string, questions: string[], answers: string[] }) {
        return this.post(this.apiUrl + '/bulk-add-qna-pairs', data)
    }

    getSmartSuggestions(questionIds: string[], answerIds: string[]) {
        return this.post<{ question: string, answer: string }>(this.apiUrl + '/smart-suggestions', { questionIds, answerIds });
    }
}
